// Thẻ của hộp hướng dẫn trang Ca lễ tân — MỘT nguồn, buộc vào nhãn thật.
//
// Nhãn lấy thẳng từ `LETAN_SHIFT_TAB_LABEL` của màn, và danh sách khoá khai
// `satisfies` theo đúng khoá của từ điển đó: đổi khoá thẻ trên màn là lỗi kiểu
// ngay lúc build, không phải đợi ai mở hộp lên mới thấy bài chỉ tới một thẻ
// không còn tồn tại.

import type { LETAN_SHIFT_TAB_LABEL } from "@/features/letan/labels";
import type { Audience } from "./audience";
import type { StatusTone } from "./GuideSections";
import type { GuideFlow, MultiTabGuideConfig } from "./multi-tab-guides";

/** Id trang trong `nav-items.ts`. Tra bằng hằng số, không gõ chuỗi lần hai. */
export const LETAN_SHIFT_GUIDE_ID = "letan_shifts";

/** Thứ tự thẻ trong hộp = thứ tự thẻ trên màn. */
export const LETAN_SHIFT_GUIDE_TAB_ORDER = [
  "register",
  "approve",
  "payroll",
] as const satisfies readonly (keyof typeof LETAN_SHIFT_TAB_LABEL)[];

export type LetanShiftGuideTabKey = (typeof LETAN_SHIFT_GUIDE_TAB_ORDER)[number];

/**
 * Nhóm nghề nào MỞ ĐƯỢC thẻ nào trên màn thật.
 *
 * Nguồn: `features/letan/permissions.ts` (`canApproveShift`) + hành động
 * `letan.approve` của máy chủ. Hai thẻ sau chung MỘT cổng quyền, nên chúng đi
 * cùng nhau — người mở được thẻ Duyệt cũng mở được thẻ Lương ca.
 *
 * Thẻ "Lương ca" nằm sau cổng duyệt là chỗ đáng chú ý nhất của trang này: nó
 * bày giờ công và đơn giá của cả quầy, nên lễ tân thường KHÔNG được vào. Bài
 * của thẻ đó cũng vì thế mà không kể gì về cách tiền ra.
 */
export const THE_AI_MO_DUOC: Record<LetanShiftGuideTabKey, Audience[]> = {
  // Thẻ đăng ký: ai vào được trang là thấy — đây là chỗ lễ tân khai ca của mình.
  register: ["reception", "qc", "lead", "exec"],
  // Hai thẻ sau cùng gác bằng `letan.approve`.
  approve: ["lead", "exec", "qc"],
  payroll: ["lead", "exec", "qc"],
};

/** Màu nhãn trạng thái, khớp badge trên màn thật. */
const TONE_THEO_NHAN: Record<string, StatusTone> = {
  "Đã duyệt": "success",
  "Chờ duyệt": "warning",
  "Đã khoá": "warning",
  "Bị từ chối": "danger",
  "Đã huỷ": "danger",
};

/**
 * Vòng đời một ca trực.
 *
 * Ba thẻ của màn là ba chặng của cùng một ca, nên dải này gần như trùng hàng
 * thẻ — và đó chính là điều đáng nói: người đăng ký hay tưởng khai xong là
 * xong, trong khi ca chỉ thành thật ở chặng sau.
 */
const LETAN_SHIFT_FLOW: GuideFlow = {
  heading: "Một ca trực đi qua đâu",
  intro:
    "Ba thẻ của trang là ba chặng của cùng một ca. Chặng tô đậm là chỗ thẻ bạn đang mở nhìn vào.",
  stages: [
    {
      ten: "Đăng ký",
      tone: "info",
      ke: "Người trực khai ngày và ca mình nhận trong tuần; khai xong ca chưa thành lịch chính thức.",
      the: ["register"],
    },
    {
      ten: "Chờ duyệt",
      tone: "warning",
      ke: "Ca nằm chờ người chốt lịch xem: nhận thì thành ca chính thức, không nhận thì trả lại kèm lý do.",
      the: ["approve"],
    },
    {
      ten: "Đã duyệt",
      tone: "success",
      ke: "Ca chính thức, cứ theo đó mà đi trực; giờ công của tuần đếm từ đúng những ca này.",
      the: ["register", "payroll"],
    },
    {
      ten: "Khoá tuần",
      tone: "danger",
      nhanh: true,
      ke: "Chốt cả tuần: từ lúc đó không ai đăng ký hay sửa được nữa, kể cả ca của chính mình.",
      the: ["register", "approve"],
    },
  ],
};

export const LETAN_SHIFT_GUIDE: MultiTabGuideConfig = {
  tabOrder: LETAN_SHIFT_GUIDE_TAB_ORDER,
  theAiMoDuoc: THE_AI_MO_DUOC,
  tone: TONE_THEO_NHAN,
  flow: LETAN_SHIFT_FLOW,
};
